import React, { useState, useEffect } from 'react';
import { Bell, Users, TrendingUp, MessageSquare, FileText, Building, Check, X, RotateCcw } from 'lucide-react';
import { API_URL } from '../config';
import './NotificationSettings.css';

const NotificationSettings = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Notification groups with their preferences
  const notificationGroups = [
    {
      title: 'User Activity',
      icon: Users,
      color: '#3b82f6',
      items: [
        {
          key: 'notify_on_user_login',
          label: 'User Login Notifications',
          description: 'Get notified when users log in to the platform'
        }
      ]
    },
    {
      title: 'Lead Generation',
      icon: TrendingUp,
      color: '#10b981',
      items: [
        {
          key: 'notify_on_lead_assessment',
          label: 'Lead Assessment Completion',
          description: 'Get notified when a new lead completes their exit readiness assessment'
        }
      ]
    },
    {
      title: 'Communication',
      icon: MessageSquare,
      color: '#8b5cf6',
      items: [
        {
          key: 'notify_on_new_message',
          label: 'New Messages',
          description: 'Get notified when you receive new messages'
        },
        {
          key: 'notify_on_new_alert',
          label: 'New Alerts',
          description: 'Get notified when new alerts are created'
        }
      ]
    },
    {
      title: 'Assessments',
      icon: FileText,
      color: '#f59e0b',
      items: [
        {
          key: 'notify_on_gap_assessment_completion',
          label: 'Gap Assessment Completion',
          description: 'Get notified when gap assessments are completed'
        },
        {
          key: 'notify_on_personal_assessment_completion',
          label: 'Personal Assessment Completion',
          description: 'Get notified when personal assessments are completed'
        },
        {
          key: 'notify_on_goal_setting_saved',
          label: 'Goals & Objectives Saved',
          description: 'Get notified when a client saves their Goals & Objectives assessment'
        }
      ]
    },
    {
      title: 'Business & Valuation',
      icon: Building,
      color: '#06b6d4',
      items: [
        {
          key: 'notify_on_business_creation',
          label: 'Business Creation',
          description: 'Get notified when new businesses are created'
        },
        {
          key: 'notify_on_valuation_creation',
          label: 'Valuation Creation',
          description: 'Get notified when new valuations are created'
        },
        {
          key: 'notify_on_valuation_update',
          label: 'Valuation Updates',
          description: 'Get notified when existing valuations are updated'
        }
      ]
    }
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notification-preferences/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notification preferences');
      }

      const data = await response.json();
      setPreferences(data.preferences);
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError('Failed to load notification preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      const payload = { [key]: value };

      const response = await fetch(`${API_URL}/notification-preferences/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update notification preference');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setSuccessMessage('Preference updated successfully');

      // Clear success message after 2 seconds
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error updating preference:', err);
      setError(err.message || 'Failed to update preference. Please try again.');

      // Revert optimistic update on error
      const response = await fetch(`${API_URL}/notification-preferences/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all notification preferences to their default values?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notification-preferences/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reset notification preferences');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setSuccessMessage('Preferences reset to defaults successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error resetting preferences:', err);
      setError('Failed to reset preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    if (!preferences || saving) return;

    const newValue = !preferences[key];

    // Optimistically update UI
    setPreferences({ ...preferences, [key]: newValue });
    // Send update to server
    updatePreference(key, newValue);
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading notification preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      <div className="settings-header">
        <div className="header-content">
          <Bell size={32} className="header-icon" />
          <div>
            <h1>Notification Settings</h1>
            <p>Manage how you receive notifications about system events</p>
          </div>
        </div>
        <button
          className="reset-button"
          onClick={resetToDefaults}
          disabled={saving}
        >
          <RotateCcw size={18} />
          Reset to Defaults
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <X size={20} />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <Check size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      <div className="notification-groups">
        {notificationGroups.map((group) => {
          const Icon = group.icon;
          return (
            <div key={group.title} className="notification-group">
              <div className="group-header" style={{ borderLeftColor: group.color }}>
                <Icon size={24} style={{ color: group.color }} />
                <h2>{group.title}</h2>
              </div>
              <div className="group-items">
                {group.items.map((item) => {
                  const isChecked = preferences?.[item.key] === true;
                  return (
                    <div key={item.key} className="notification-item">
                      <div className="item-info">
                        <label htmlFor={item.key}>{item.label}</label>
                        <p>{item.description}</p>
                      </div>
                      <div
                        className="toggle-switch"
                        onClick={() => !saving && handleToggle(item.key)}
                        style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={isChecked}
                          onChange={() => {}} // Controlled by parent div onClick
                          disabled={saving}
                        />
                        <span className="slider"></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="settings-footer">
        <p className="footer-note">
          Notifications are sent to your registered email address. Changes take effect immediately.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;
